/**
 * Sparkofy Gallery Masonry v2.1
 * - Pinterest-style masonry (CSS columns + natural image aspect ratios)
 * - Tag groups (案場/型號/其他分類) with wrap layout + gray background
 * - Top bar: Search + All + Union/Intersection toggle
 * - Tag toggle: click to select, click again to deselect
 * - Multi-tag support: union (OR) or intersection (AND)
 * - Lightbox with keyboard nav, swipe, thumbnails
 * - Scroll-based lazy loading
 * - Anti-Kalles: WeakMap for URLs + MutationObserver to restore img src
 */
(function() {
  'use strict';

  var GALLERY_SEL = '.spk-gallery';
  var imgUrls = new WeakMap();

  function init() {
    document.querySelectorAll(GALLERY_SEL).forEach(initGallery);
  }

  function initGallery(container) {
    var dataEl = container.querySelector('script[type="application/json"]');
    if (dataEl) {
      try { renderGallery(container, JSON.parse(dataEl.textContent)); } catch(e) {}
    } else {
      var galleryId = container.dataset.galleryId;
      if (!galleryId) return;
      var base = window.__galleryAssetBase || '/assets/';
      fetch(base + galleryId + '.json?v=' + Date.now())
        .then(function(r) { return r.json(); })
        .then(function(data) { renderGallery(container, data); })
        .catch(function(e) { console.error('[Gallery] Load failed:', e); });
    }
  }

  function renderGallery(container, data) {
    var images = data.images || [];
    if (!images.length) return;

    var tags = data.tags || [];
    var tagGroups = data.tag_groups || null;
    var state = { activeTags: [], filteredImages: images, lightboxIndex: -1, mode: 'union' };

    // Read initial tag from URL hash
    var hash = decodeURIComponent(location.hash.slice(1));
    if (hash && tags.some(function(t) { return t.name === hash; })) {
      state.activeTags = [hash];
      state.filteredImages = filterByTags(images, state.activeTags, state.mode);
    }

    var topBar = buildTopBar(tags, images.length, state);
    var filterEl = buildFilters(tags, state, tagGroups);
    var gridEl = buildGrid(state.filteredImages);
    var lightbox = buildLightbox();

    container.innerHTML = '';
    container.appendChild(topBar);
    container.appendChild(filterEl);
    container.appendChild(gridEl);
    container.appendChild(lightbox.el);

    setupKallesFighter(gridEl);
    setupTopBarEvents(topBar, filterEl, tags, images, state, container, lightbox);
    setupFilterEvents(filterEl, tags, images, state, container, lightbox, topBar);
    setupGridEvents(gridEl, state, lightbox);
    setupLightboxEvents(lightbox, state);
    setupLazyLoad(gridEl, state.filteredImages);

    window.addEventListener('hashchange', function() {
      var h = decodeURIComponent(location.hash.slice(1));
      if (h && tags.some(function(t) { return t.name === h; })) {
        state.activeTags = [h];
        refreshGrid(filterEl, tags, images, state, container, lightbox);
      } else if (!h) {
        state.activeTags = [];
        refreshGrid(filterEl, tags, images, state, container, lightbox);
      }
    });
  }

  function filterByTags(images, activeTags, mode) {
    if (!activeTags.length) return images;
    if (mode === 'intersection') {
      return images.filter(function(img) {
        return activeTags.every(function(t) { return img.tags && img.tags.includes(t); });
      });
    } else {
      return images.filter(function(img) {
        return activeTags.some(function(t) { return img.tags && img.tags.includes(t); });
      });
    }
  }

  /* ── Kalles Fighter ── */
  function setupKallesFighter(gridEl) {
    var mo = new MutationObserver(function(mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];
        var target = m.target;
        if (target.tagName !== 'IMG') continue;
        var realUrl = imgUrls.get(target);
        if (!realUrl) continue;
        if (m.attributeName === 'src' && target.getAttribute('src') !== realUrl) {
          target.setAttribute('src', realUrl);
        }
        if (m.attributeName === 'class' && target.classList.contains('hideImg')) {
          target.classList.remove('hideImg');
          target.style.opacity = '';
        }
        if (m.attributeName === 'srcset') {
          target.removeAttribute('srcset');
        }
      }
    });
    mo.observe(gridEl, { subtree: true, attributes: true, attributeFilter: ['src', 'class', 'srcset'] });
    return mo;
  }

  /* ── Top Bar: Search + All + Mode Toggle ── */
  function buildTopBar(tags, total, state) {
    var bar = el('div', 'spk-gallery__top-bar');

    var searchInput = el('input', 'spk-gallery__search');
    searchInput.type = 'text';
    searchInput.placeholder = '搜尋標籤...';
    bar.appendChild(searchInput);

    bar.appendChild(mkBtn('All', total, !state.activeTags.length));

    var modeToggle = el('div', 'spk-gallery__mode-toggle');
    var unionBtn = el('button', 'spk-gallery__mode-btn' + (state.mode === 'union' ? ' spk-gallery__mode-btn--active' : ''));
    unionBtn.textContent = '聯集';
    unionBtn.dataset.mode = 'union';
    var interBtn = el('button', 'spk-gallery__mode-btn' + (state.mode === 'intersection' ? ' spk-gallery__mode-btn--active' : ''));
    interBtn.textContent = '交集';
    interBtn.dataset.mode = 'intersection';
    modeToggle.appendChild(unionBtn);
    modeToggle.appendChild(interBtn);
    bar.appendChild(modeToggle);

    return bar;
  }

  function setupTopBarEvents(topBar, filterEl, tags, images, state, container, lightbox) {
    // All button click
    topBar.addEventListener('click', function(e) {
      var btn = e.target.closest('.spk-gallery__tag');
      if (btn && btn.dataset.tag === 'All') {
        state.activeTags = [];
        refreshGrid(filterEl, tags, images, state, container, lightbox);
        updateActiveStyles(filterEl, topBar, state);
        history.replaceState(null, '', location.pathname + location.search);
        return;
      }
      // Mode toggle
      var modeBtn = e.target.closest('.spk-gallery__mode-btn');
      if (modeBtn) {
        state.mode = modeBtn.dataset.mode;
        topBar.querySelectorAll('.spk-gallery__mode-btn').forEach(function(b) {
          b.classList.toggle('spk-gallery__mode-btn--active', b.dataset.mode === state.mode);
        });
        if (state.activeTags.length) {
          refreshGrid(filterEl, tags, images, state, container, lightbox);
        }
      }
    });

    // Search input
    var searchInput = topBar.querySelector('.spk-gallery__search');
    if (searchInput) {
      searchInput.addEventListener('input', function() {
        var q = searchInput.value.trim().toLowerCase();
        filterEl.querySelectorAll('.spk-gallery__tag').forEach(function(btn) {
          var match = !q || btn.dataset.tag.toLowerCase().includes(q);
          btn.style.display = match ? '' : 'none';
        });
        filterEl.querySelectorAll('.spk-gallery__filter-group').forEach(function(group) {
          var visibleBtns = group.querySelectorAll('.spk-gallery__tag:not([style*="display: none"])');
          group.style.display = visibleBtns.length ? '' : 'none';
        });
      });
    }
  }

  /* ── Filters ── */
  function buildFilters(tags, state, tagGroups) {
    var wrap = el('div', 'spk-gallery__filters');

    if (tagGroups && tagGroups.length) {
      tagGroups.forEach(function(group) {
        var row = el('div', 'spk-gallery__filter-group');
        var label = el('span', 'spk-gallery__group-label');
        label.textContent = group.label;
        row.appendChild(label);
        var track = el('div', 'spk-gallery__group-track');
        group.tags.forEach(function(tagName) {
          var tagObj = tags.find(function(t) { return t.name === tagName; });
          if (tagObj) {
            track.appendChild(mkBtn(tagObj.name, tagObj.count, state.activeTags.indexOf(tagObj.name) >= 0));
          }
        });
        row.appendChild(track);
        wrap.appendChild(row);
      });
    } else {
      var row = el('div', 'spk-gallery__filter-row spk-gallery__filter-row--flat');
      tags.forEach(function(t) {
        row.appendChild(mkBtn(t.name, t.count, state.activeTags.indexOf(t.name) >= 0));
      });
      wrap.appendChild(row);
    }

    return wrap;
  }

  function mkBtn(name, count, active) {
    var b = el('button', 'spk-gallery__tag' + (active ? ' spk-gallery__tag--active' : ''));
    b.textContent = name;
    var badge = el('span', 'spk-gallery__tag-count');
    badge.textContent = count;
    b.appendChild(badge);
    b.dataset.tag = name;
    return b;
  }

  function setupFilterEvents(filterEl, tags, images, state, container, lightbox, topBar) {
    filterEl.addEventListener('click', function(e) {
      var btn = e.target.closest('.spk-gallery__tag');
      if (!btn) return;
      var tag = btn.dataset.tag;

      // Toggle tag
      var idx = state.activeTags.indexOf(tag);
      if (idx >= 0) {
        state.activeTags.splice(idx, 1);
      } else {
        state.activeTags.push(tag);
      }

      refreshGrid(filterEl, tags, images, state, container, lightbox);
      updateActiveStyles(filterEl, topBar, state);

      // Update URL hash
      if (state.activeTags.length === 1) {
        history.replaceState(null, '', '#' + encodeURIComponent(state.activeTags[0]));
      } else if (state.activeTags.length === 0) {
        history.replaceState(null, '', location.pathname + location.search);
      }

      // Clear search
      var searchInput = topBar.querySelector('.spk-gallery__search');
      if (searchInput && searchInput.value) {
        searchInput.value = '';
        searchInput.dispatchEvent(new Event('input'));
      }
    });
  }

  function updateActiveStyles(filterEl, topBar, state) {
    // Update tag buttons
    var allBtns = [];
    filterEl.querySelectorAll('.spk-gallery__tag').forEach(function(b) { allBtns.push(b); });
    topBar.querySelectorAll('.spk-gallery__tag').forEach(function(b) { allBtns.push(b); });
    allBtns.forEach(function(b) {
      if (b.dataset.tag === 'All') {
        b.classList.toggle('spk-gallery__tag--active', state.activeTags.length === 0);
      } else {
        b.classList.toggle('spk-gallery__tag--active', state.activeTags.indexOf(b.dataset.tag) >= 0);
      }
    });
  }

  function refreshGrid(filterEl, tags, images, state, container, lightbox) {
    state.filteredImages = filterByTags(images, state.activeTags, state.mode);
    var oldGrid = container.querySelector('.spk-gallery__grid');
    var newGrid = buildGrid(state.filteredImages);
    if (oldGrid) oldGrid.replaceWith(newGrid);
    setupKallesFighter(newGrid);
    setupGridEvents(newGrid, state, lightbox);
    setupLazyLoad(newGrid, state.filteredImages);
  }

  /* ── Grid ── */
  function buildGrid(images) {
    var grid = el('div', 'spk-gallery__grid');
    images.forEach(function(img, i) {
      var item = el('div', 'spk-gallery__item');
      item.dataset.index = i;

      var placeholder = el('div', 'spk-gallery__img-placeholder');
      item.appendChild(placeholder);

      var overlay = el('div', 'spk-gallery__overlay');
      var plus = el('span', 'spk-gallery__plus');
      plus.innerHTML = '&#43;';
      overlay.appendChild(plus);
      item.appendChild(overlay);

      if (img.tags && img.tags.length) {
        var caption = el('div', 'spk-gallery__caption');
        caption.textContent = img.tags.join(', ');
        item.appendChild(caption);
      }

      grid.appendChild(item);
    });
    return grid;
  }

  function setupGridEvents(gridEl, state, lightbox) {
    gridEl.addEventListener('click', function(e) {
      var item = e.target.closest('.spk-gallery__item');
      if (!item) return;
      openLightbox(lightbox, state, parseInt(item.dataset.index));
    });
  }

  /* ── Lazy Load ── */
  function setupLazyLoad(gridEl, images) {
    var pending = [];
    gridEl.querySelectorAll('.spk-gallery__item').forEach(function(item) { pending.push(item); });

    function loadVisible() {
      var vh = window.innerHeight;
      var remaining = [];
      for (var i = 0; i < pending.length; i++) {
        var item = pending[i];
        var rect = item.getBoundingClientRect();
        if (rect.top < vh + 800 && rect.bottom > -400) {
          var idx = parseInt(item.dataset.index);
          var img = images[idx];
          if (!img) continue;
          var url = img.src;
          var ph = item.querySelector('.spk-gallery__img-placeholder');
          if (!ph) continue;

          var imgEl = document.createElement('img');
          imgEl.className = 'spk-gallery__img';
          imgEl.alt = img.alt || '';
          imgEl.loading = 'lazy';
          imgEl.onload = function() { this.classList.add('spk-gallery__img--loaded'); };
          imgUrls.set(imgEl, url);
          imgEl.src = url;
          ph.replaceWith(imgEl);
        } else {
          remaining.push(item);
        }
      }
      pending = remaining;
      if (!pending.length && scrollHandler) {
        window.removeEventListener('scroll', scrollHandler);
        scrollHandler = null;
      }
    }

    var scrollTimer = null;
    var scrollHandler = function() {
      if (scrollTimer) return;
      scrollTimer = setTimeout(function() { scrollTimer = null; loadVisible(); }, 100);
    };
    window.addEventListener('scroll', scrollHandler, { passive: true });

    var fixAttempts = 0;
    var fixTimer = setInterval(function() {
      gridEl.querySelectorAll('img.spk-gallery__img').forEach(function(imgEl) {
        var realUrl = imgUrls.get(imgEl);
        if (!realUrl) return;
        if (imgEl.getAttribute('src') !== realUrl) imgEl.setAttribute('src', realUrl);
        if (imgEl.classList.contains('hideImg')) {
          imgEl.classList.remove('hideImg');
          imgEl.style.opacity = '';
        }
        var ds = imgEl.getAttribute('data-src');
        if (ds) imgEl.removeAttribute('data-src');
        var ss = imgEl.getAttribute('srcset');
        if (ss) imgEl.removeAttribute('srcset');
      });
      fixAttempts++;
      if (fixAttempts >= 20) clearInterval(fixTimer);
    }, 500);

    loadVisible();
    setTimeout(loadVisible, 500);
    setTimeout(loadVisible, 1500);
    setTimeout(loadVisible, 3000);
  }

  /* ── Lightbox ── */
  function buildLightbox() {
    var lb = el('div', 'spk-gallery__lightbox');
    lb.innerHTML =
      '<div class="spk-lb__backdrop"></div>' +
      '<button class="spk-lb__close">&times;</button>' +
      '<button class="spk-lb__nav spk-lb__nav--prev"><svg viewBox="0 0 24 24"><polyline points="15 18 9 12 15 6"/></svg></button>' +
      '<button class="spk-lb__nav spk-lb__nav--next"><svg viewBox="0 0 24 24"><polyline points="9 18 15 12 9 6"/></svg></button>' +
      '<div class="spk-lb__main"><img class="spk-lb__img" src="" alt=""></div>' +
      '<div class="spk-lb__info"></div>' +
      '<div class="spk-lb__thumbs"><div class="spk-lb__thumbs-track"></div></div>';
    return { el: lb };
  }

  function openLightbox(lightbox, state, index) {
    var lb = lightbox.el;
    if (!lb) return;
    state.lightboxIndex = index;
    lb.classList.add('spk-lb--open');
    document.body.style.overflow = 'hidden';
    updateLightbox(lb, state);
    buildThumbs(lb, state);
  }

  function closeLightbox(lb, state) {
    lb.classList.remove('spk-lb--open');
    document.body.style.overflow = '';
    state.lightboxIndex = -1;
  }

  function updateLightbox(lb, state) {
    var img = state.filteredImages[state.lightboxIndex];
    if (!img) return;
    var mainImg = lb.querySelector('.spk-lb__img');
    mainImg.src = img.src;
    mainImg.alt = img.alt || '';

    lb.querySelector('.spk-lb__info').textContent = img.tags ? img.tags.join(', ') : '';

    lb.querySelectorAll('.spk-lb__thumb').forEach(function(t, i) {
      t.classList.toggle('spk-lb__thumb--active', i === state.lightboxIndex);
    });
    var activeThumb = lb.querySelector('.spk-lb__thumb--active');
    if (activeThumb) activeThumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });

    lb.querySelector('.spk-lb__nav--prev').style.display = state.lightboxIndex > 0 ? '' : 'none';
    lb.querySelector('.spk-lb__nav--next').style.display = state.lightboxIndex < state.filteredImages.length - 1 ? '' : 'none';
  }

  function buildThumbs(lb, state) {
    var track = lb.querySelector('.spk-lb__thumbs-track');
    track.innerHTML = '';
    state.filteredImages.forEach(function(img, i) {
      var thumb = el('div', 'spk-lb__thumb' + (i === state.lightboxIndex ? ' spk-lb__thumb--active' : ''));
      var thumbBg = el('div');
      thumbBg.style.cssText = 'width:100%;height:100%;background-image:url(' + encodeURI(img.src) + ');background-size:cover;background-position:center';
      thumb.appendChild(thumbBg);
      thumb.dataset.index = i;
      track.appendChild(thumb);
    });
  }

  function setupLightboxEvents(lightbox, state) {
    var lb = lightbox.el;
    lb.addEventListener('click', function(e) {
      if (e.target.closest('.spk-lb__close') || e.target.closest('.spk-lb__backdrop')) closeLightbox(lb, state);
      if (e.target.closest('.spk-lb__nav--prev') && state.lightboxIndex > 0) { state.lightboxIndex--; updateLightbox(lb, state); }
      if (e.target.closest('.spk-lb__nav--next') && state.lightboxIndex < state.filteredImages.length - 1) { state.lightboxIndex++; updateLightbox(lb, state); }
      var thumb = e.target.closest('.spk-lb__thumb');
      if (thumb) { state.lightboxIndex = parseInt(thumb.dataset.index); updateLightbox(lb, state); }
    });
    document.addEventListener('keydown', function(e) {
      if (!lb.classList.contains('spk-lb--open')) return;
      if (e.key === 'Escape') closeLightbox(lb, state);
      if (e.key === 'ArrowLeft' && state.lightboxIndex > 0) { state.lightboxIndex--; updateLightbox(lb, state); }
      if (e.key === 'ArrowRight' && state.lightboxIndex < state.filteredImages.length - 1) { state.lightboxIndex++; updateLightbox(lb, state); }
    });
    var touchStartX = 0;
    lb.addEventListener('touchstart', function(e) { touchStartX = e.touches[0].clientX; }, { passive: true });
    lb.addEventListener('touchend', function(e) {
      var dx = e.changedTouches[0].clientX - touchStartX;
      if (Math.abs(dx) < 50) return;
      if (dx > 0 && state.lightboxIndex > 0) { state.lightboxIndex--; updateLightbox(lb, state); }
      else if (dx < 0 && state.lightboxIndex < state.filteredImages.length - 1) { state.lightboxIndex++; updateLightbox(lb, state); }
    }, { passive: true });
  }

  /* ── Helpers ── */
  function el(tag, cls) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    return e;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
